/**
 * Authentication API
 * 
 * Handles user authentication, session management, and profile data.
 */

import { apiPost, apiPatch, ApiError } from '../api';

// Onboarding context (v0.9.5 - Issue #563)
export type OnboardingContext =
  | { type: 'club'; club_id: string; club_name: string }
  | { type: 'event'; event_id: string; event_name: string }
  | { type: 'founder'; club_id?: string; club_name?: string }
  | { type: 'clean' };

export interface Me {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  public_name_mode: string;
  public_display_name: string;
  name: string | null;
  avatar_url: string | null;
  tagline: string | null;
  birthday_day: number | null;
  birthday_month: number | null;
  location: string | null;
  primary_sport: string | null;
  secondary_sports: string[] | null;
  profile_image_url: string | null;
  user_level: string;
  subscription_level: string | null;
  // Platform admin system (v0.10.0 - Issue #213)
  is_platform_admin: boolean; // Converted from SQLite number (0/1) to boolean
  global_role: string | null; // 'platform_admin' | 'trust_safety' | 'support' | 'auditor' | 'billing' | null
  // Onboarding tracking (v0.9.5 - Issue #563)
  onboarding_completed: boolean;
  onboarding_context?: OnboardingContext; // Optional - only present if onboarding needed
  created_at: string;
}

export interface LoginPayload {
  email: string;
  token: string;
}

export interface LoginResponse {
  success: boolean;
}

export interface MagicLinkRequestPayload {
  email: string;
  turnstile_token?: string;
}

export interface MagicLinkRequestResponse {
  success: boolean;
}

export interface MagicLinkConsumePayload {
  token: string;
}

export interface MagicLinkConsumeResponse {
  user: Me;
}

export const authApi = {
  /**
   * Get current user profile
   * GET /auth/me
   * 
   * SPECIAL CASE: 401/403 on /auth/me is a VALID, EXPECTED state for guests.
   * Do NOT convert it to an ApiError here — that breaks public pages.
   * Returns null instead of throwing when unauthenticated.
   */
  me: async (): Promise<Me | null> => {
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || '/api';
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      // Guest user – totally valid on public pages
      if (response.status === 401 || response.status === 403) {
        return null;
      }

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new ApiError(
          body.error?.message || 'Failed to fetch user profile',
          body.error?.code || 'UNKNOWN_ERROR',
          response.status,
          body.error?.details
        );
      }

      // Backend wraps user in { user: Me } envelope
      const payload = await response.json();
      return payload.user ?? payload ?? null;
    } catch (err) {
      if (err instanceof ApiError) throw err; // Let real API errors bubble
      // Network error, JSON parse error, etc. → treat as unauthenticated
      return null;
    }
  },

  /**
   * Login with magic link token
   * POST /auth/login
   */
  login: (payload: LoginPayload) => apiPost<LoginResponse>('/auth/login', payload),

  /**
   * Logout current session
   * POST /auth/logout
   */
  logout: () => apiPost<void>('/auth/logout'),

  /**
   * Request magic link email
   * POST /auth/magic-link/request
   */
  requestMagicLink: (email: string, turnstileToken?: string) => 
    apiPost<MagicLinkRequestResponse>('/auth/magic-link/request', { 
      email, 
      ...(turnstileToken && { turnstile_token: turnstileToken })
    }),

  /**
   * Consume magic link token and establish session
   * POST /auth/magic-link/consume
   */
  consumeMagicLink: (token: string) =>
    apiPost<MagicLinkConsumeResponse>('/auth/magic-link/consume', { token }),

  /**
   * Mark onboarding as complete
   * PATCH /auth/onboarding/complete
   */
  completeOnboarding: () =>
    apiPatch<{ success: boolean }>('/auth/onboarding/complete'),
};
